/**
 * AlertToastProvider — client-side, real-time in-app toasts for full-filter alerts.
 *
 * This is now the SINGLE in-app alert-toast source. The legacy InAppAlertProvider —
 * which toasted off server-published `alert` WebSocket events — has been retired so
 * alerts don't double-toast.
 *
 * AlertToastProvider matches the NEW saved-filter alerts CLIENT-SIDE against the
 * live message stream, so they fire ephemerally BEFORE Plan 2's server-side durable
 * matcher exists. These toasts are best-effort and not durable.
 *
 * Coverage: this provider opens its OWN always-on `/ws/messages` subscription via
 * useMessageSubscription — independent of audio. It is enabled whenever the org has
 * active channels, so alert toasts fire APP-WIDE (not just while live audio / drip is
 * playing). This mirrors how the retired InAppAlertProvider ran its own socket.
 * Opening this connection in addition to LiveFeedProvider's audio-gated one is
 * intentional; the two sockets are not shared/deduped.
 *
 * It is a side-effect provider (renders children passthrough):
 * useToast, dedupe ref, navigate-on-click.
 *
 * Alerts are loaded on mount / org change AND re-fetched on an interval
 * (ALERTS_REFRESH_MS), so alerts created/edited/activated after mount start matching
 * without a full page reload. The dedupe set is preserved across refreshes so a
 * refresh never re-toasts an already-seen message.
 */

import React, { createContext, useContext, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import type { OrgMessage } from '../../common/entities';
import type { Alert } from '../../api/server/alerts';
import { listAlerts } from '../../api/server/alerts';
import { getFilterSettings } from '../../api/server/groups';
import { useToast } from '../components/ui/Toast';
import { useOrg } from './OrgProvider';
import { useMessageSubscription } from '../hooks/useMessageSubscription';
import { UNGROUPED_TO_FILTER_FIELD } from '../hooks/useGroupData';
import type { FilterField, MessageFilters } from '../hooks/useMessageFilters';
import { matchesFilters } from '../hooks/useMessageFilters';
import { persistedToMessageFilters } from '../alerts/persistedFilter';
import { logger } from '../../utils/logger';

// =============================================================================
// keywordMatches — BEST-EFFORT client-side keyword check
// =============================================================================

/**
 * Best-effort keyword matcher for ephemeral toasts. This is NOT a faithful mirror
 * of the server's Postgres FTS query — it only needs to be "good enough" to decide
 * whether to surface a transient in-app toast. The durable match still happens
 * server-side (Plan 2). Do not rely on this for correctness.
 *
 * Supported (intentionally simple) syntax:
 *   - empty / undefined / whitespace-only keyword  → always matches (true)
 *   - "quoted phrase"  → case-insensitive SUBSTRING match of the phrase
 *   - -negation        → if the negated word is present, the whole match FAILS
 *   - plain words      → OR semantics: match if ANY plain word appears
 *                        (case-insensitive, with word-ish boundaries)
 *
 * Evaluation order: negations are checked first (any hit → false), then phrases and
 * plain words are OR'd together. If there are no positive terms (only negations),
 * a message that survives the negations matches.
 */
/**
 * Delivery policy: never toast alerts that opted out of in-app notifications, and
 * never toast firehose/export (unlimited) alerts — a misconfigured export must not
 * toast-storm the org even if inApp was left on.
 */
export function shouldSuppressToast(alert: Pick<Alert, 'notify_policy'>): boolean {
  return alert.notify_policy?.inApp === false || alert.notify_policy?.unlimited === true;
}

export function keywordMatches(text: string | undefined | null, keyword?: string | null): boolean {
  const kw = (keyword ?? '').trim();
  if (kw === '') return true;

  const haystack = (text ?? '').toLowerCase();

  // Extract "quoted phrases" first, removing them from the remaining token stream.
  const phrases: string[] = [];
  const withoutPhrases = kw.replace(/"([^"]+)"/g, (_match, phrase: string) => {
    phrases.push(phrase.toLowerCase());
    return ' ';
  });

  // Remaining whitespace-separated tokens: negations (-word) and plain words.
  const tokens = withoutPhrases.split(/\s+/).filter(Boolean);
  const negations: string[] = [];
  const plainWords: string[] = [];
  for (const tok of tokens) {
    if (tok.startsWith('-') && tok.length > 1) {
      negations.push(tok.slice(1).toLowerCase());
    } else {
      plainWords.push(tok.toLowerCase());
    }
  }

  // Word-ish boundary match: the word appears not glued to other word characters.
  const wordPresent = (word: string): boolean => {
    const escaped = word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return new RegExp(`(^|[^a-z0-9])${escaped}([^a-z0-9]|$)`, 'i').test(haystack);
  };

  // Negations are hard fails.
  for (const neg of negations) {
    if (wordPresent(neg)) return false;
  }

  const positiveTerms = phrases.length + plainWords.length;
  // Only negations were supplied and none hit → it's a match.
  if (positiveTerms === 0) return true;

  // Phrases: case-insensitive substring.
  for (const phrase of phrases) {
    if (haystack.includes(phrase)) return true;
  }
  // Plain words: OR with word-ish boundaries.
  for (const word of plainWords) {
    if (wordPresent(word)) return true;
  }

  return false;
}

// =============================================================================
// Provider
// =============================================================================

interface AlertToastContextValue {
  /** Number of active alerts currently being matched (debug / introspection). */
  activeAlertCount: number;
  /** Synchronously check a message against active alerts; returns matched alert info. */
  checkMessage: (msg: OrgMessage) => Array<{ alert_id: string; name: string }>;
}

const AlertToastContext = createContext<AlertToastContextValue | null>(null);

export const useAlertToast = (): AlertToastContextValue => {
  const context = useContext(AlertToastContext);
  if (!context) {
    throw new Error('useAlertToast must be used within an AlertToastProvider');
  }
  return context;
};

interface AlertToastProviderProps {
  children: React.ReactNode;
}

/** Truncate text for toast message bodies. */
function truncateText(text: string, maxLength = 80): string {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength - 3) + '...';
}

// Cap the dedupe set. This provider runs an always-on app-wide subscription, so on
// a busy org the `${talk_id}:${alert_id}` set would otherwise grow unbounded for the
// whole session. Dedupe only needs a recent horizon, so we keep a FIFO window of the
// most recent keys — enough to stop a message re-toasting the same alert.
const SEEN_IDS_CAP = 5000;

// Re-fetch the active-alert set on this cadence so alerts created/edited/activated
// after mount start matching without a full page reload. Dedupe state is preserved
// across refreshes; only the alert set + compiled-filter cache are rebuilt.
const ALERTS_REFRESH_MS = 60000;

export const AlertToastProvider: React.FC<AlertToastProviderProps> = ({ children }) => {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { currentOrg, groups, channels, channelsMap } = useOrg();
  const orgId = currentOrg?.org_id;

  // Active channel IDs (status === 1) for this org — drives our own always-on
  // /ws/messages subscription, independent of audio state.
  const channelIds = useMemo(
    () => channels.filter((c) => c.status === 1).map((c) => c.channel_id),
    [channels]
  );

  // Active alerts (status === 1) loaded on mount / org change.
  const activeAlertsRef = useRef<Alert[]>([]);
  const [activeAlertCount, setActiveAlertCount] = React.useState(0);

  // Resolved groupable FilterField for this org (default_group_type → camelCase field).
  const groupableFieldRef = useRef<FilterField>('channels');

  // Memoized compiled MessageFilters per alert, keyed by alert_id, so we don't
  // recompute persistedToMessageFilters on every incoming message.
  const compiledFiltersRef = useRef<Map<string, MessageFilters>>(new Map());

  // Stable signature of the org's groups (id + last-modified, so a criteria edit
  // counts as a change). compileFilters resolves group selections against `groups`,
  // which load async from useOrg(); without invalidation, an alert compiled BEFORE
  // groups arrive caches an empty groupable (an empty include reads as "no
  // constraint" → over-fires for group orgs) for the rest of the session.
  const groupsKey = useMemo(
    () => groups.map((g) => `${g.group_id}:${g.modified}`).join(','),
    [groups]
  );

  // Dedupe key: `${talk_id}:${alert_id}` — same message can fire multiple distinct
  // alerts, but never the same alert twice.
  const seenIdsRef = useRef<Set<string>>(new Set());

  // Keep latest channelsMap available to the (long-lived) message listener without
  // forcing it to re-subscribe each render.
  const channelsRef = useRef(channelsMap);
  channelsRef.current = channelsMap;

  const getChannelName = useCallback((channelId: string): string => {
    const channel = channelsRef.current[channelId];
    return channel?.name || channelId.substring(0, 8) + '...';
  }, []);

  // Latest orgId, readable inside async refreshes to discard results after a switch.
  const orgIdRef = useRef(orgId);
  orgIdRef.current = orgId;

  // Re-fetch the active-alert set. Rebuilds the alert list + compiled-filter cache
  // (so created/edited/activated alerts match) but PRESERVES the dedupe set, so a
  // refresh never re-toasts an already-seen message.
  const refreshActiveAlerts = useCallback(async () => {
    if (!orgId) return;
    try {
      const alerts = await listAlerts(orgId);
      if (orgIdRef.current !== orgId) return; // org switched mid-flight
      const active = alerts.filter((a) => a.status === 1);
      activeAlertsRef.current = active;
      compiledFiltersRef.current.clear();
      setActiveAlertCount(active.length);
    } catch (err) {
      logger.error('[AlertToast] Failed to refresh alerts:', err);
    }
  }, [orgId]);

  // Resolve groupable field + do the initial alert load on mount / org change.
  useEffect(() => {
    if (!orgId) {
      activeAlertsRef.current = [];
      compiledFiltersRef.current.clear();
      seenIdsRef.current.clear();
      setActiveAlertCount(0);
      return;
    }

    let cancelled = false;
    // New org → clear the matched set + dedupe SYNCHRONOUSLY, so a message arriving
    // during the async groupable/alert resolution below can't match the previous
    // org's alerts (which would otherwise toast un-deduped). refreshActiveAlerts
    // repopulates once it resolves. Periodic refreshes intentionally keep the dedupe.
    activeAlertsRef.current = [];
    compiledFiltersRef.current.clear();
    seenIdsRef.current.clear();
    setActiveAlertCount(0);

    (async () => {
      // Resolve the org's groupable field exactly as AlertEditor / alerts page do.
      try {
        const settings = await getFilterSettings(orgId);
        const field = settings.default_group_type || 'channels';
        if (!cancelled) {
          groupableFieldRef.current = (UNGROUPED_TO_FILTER_FIELD[field] ??
            'channels') as FilterField;
        }
      } catch (err) {
        if (!cancelled) {
          logger.error('[AlertToast] Failed to resolve groupable field:', err);
          groupableFieldRef.current = 'channels';
        }
      }
      if (!cancelled) await refreshActiveAlerts();
    })();

    return () => {
      cancelled = true;
    };
  }, [orgId, refreshActiveAlerts]);

  // Periodic refresh so alerts created/edited after mount start matching without a
  // full page reload (previously a documented v1 limitation).
  useEffect(() => {
    if (!orgId) return;
    const id = setInterval(() => {
      void refreshActiveAlerts();
    }, ALERTS_REFRESH_MS);
    return () => clearInterval(id);
  }, [orgId, refreshActiveAlerts]);

  // Drop the compiled cache whenever the org's groups change (notably the async
  // empty → populated transition on load). Any alert compiled against stale/empty
  // groups gets recompiled against the resolved set on the next message.
  useEffect(() => {
    compiledFiltersRef.current.clear();
  }, [groupsKey]);

  // Compile (and cache) a MessageFilters for an alert.
  const compileFilters = useCallback(
    (alert: Alert): MessageFilters => {
      const cached = compiledFiltersRef.current.get(alert.alert_id);
      if (cached) return cached;
      const { filters } = persistedToMessageFilters(alert.filter, {
        groupableField: groupableFieldRef.current,
        groups
      });
      compiledFiltersRef.current.set(alert.alert_id, filters);
      return filters;
    },
    [groups]
  );

  // Match an incoming message client-side against the active alerts.
  const handleMessage = useCallback(
    (msg: OrgMessage) => {
      const talkId = msg.talk_id ?? msg.id;
      if (!talkId) return;

      for (const alert of activeAlertsRef.current) {
        const dedupeKey = `${talkId}:${alert.alert_id}`;
        if (shouldSuppressToast(alert)) continue;
        if (seenIdsRef.current.has(dedupeKey)) continue;

        let filters: MessageFilters;
        try {
          filters = compileFilters(alert);
        } catch (err) {
          logger.error('[AlertToast] Failed to compile filters for alert:', alert.alert_id, err);
          continue;
        }

        const fieldsMatch = matchesFilters(msg, filters);
        const keywordOk = keywordMatches(msg.text, alert.filter.keyword);
        if (!fieldsMatch || !keywordOk) continue;

        seenIdsRef.current.add(dedupeKey);
        // FIFO eviction: a Set preserves insertion order, so drop the oldest key
        // once we exceed the cap. Keeps the dedupe window bounded over a long session.
        if (seenIdsRef.current.size > SEEN_IDS_CAP) {
          const oldest = seenIdsRef.current.values().next().value;
          if (oldest !== undefined) seenIdsRef.current.delete(oldest);
        }
        addToast({
          type: 'alert',
          title: `Alert: ${alert.name}`,
          message: `${getChannelName(msg.channel_id)}: ${truncateText(msg.text)}`,
          duration: 10000,
          onClick: () => {
            navigate(`/v2/organizations/${msg.org_id}/channels?selected=${msg.channel_id}`);
          }
        });
      }
    },
    [compileFilters, addToast, getChannelName, navigate]
  );

  // Synchronous check: which active alerts match this message?
  // Used by the Live page to annotate messages with bell icons + popover.
  const checkMessage = useCallback(
    (msg: OrgMessage): Array<{ alert_id: string; name: string }> => {
      const matches: Array<{ alert_id: string; name: string }> = [];
      for (const alert of activeAlertsRef.current) {
        let filters: MessageFilters;
        try {
          filters = compileFilters(alert);
        } catch (err) {
          logger.error('[AlertToast] Failed to compile filters for alert %s:', alert.alert_id, err);
          continue;
        }
        if (!matchesFilters(msg, filters)) continue;
        if (!keywordMatches(msg.text, alert.filter.keyword)) continue;
        matches.push({ alert_id: alert.alert_id, name: alert.name });
      }
      return matches;
    },
    [compileFilters]
  );

  // Open our OWN always-on /ws/messages subscription, independent of audio.
  // Enabled whenever the org is loaded with at least one active channel, so alert
  // toasts fire app-wide. The hook owns the WebSocket lifecycle + cleanup and reacts
  // to channelIds changes internally (no manual updateChannels call needed here).
  useMessageSubscription({
    orgId: orgId || '',
    channelIds,
    onMessage: handleMessage,
    enabled: Boolean(orgId) && channelIds.length > 0
  });

  const value = useMemo<AlertToastContextValue>(
    () => ({ activeAlertCount, checkMessage }),
    [activeAlertCount, checkMessage]
  );

  return <AlertToastContext.Provider value={value}>{children}</AlertToastContext.Provider>;
};

export default AlertToastProvider;
